import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { RegistrationService } from "src/app/core/api-services/user-profile/registration.service";
import { AuthorityOwnerModel, UserProfileLookups } from "src/app/core/models/user-profile/user-profile.model";
import { BaseComponent } from "src/app/shared/base/BaseComponent";
import { AlertImportanceEnum } from "src/app/shared/bs-alert/AlertImportanceEnum";
import { AlertOptions } from "src/app/shared/bs-alert/AlertOptions";
import { InputPatterns } from "src/app/shared/input-components/validators/InputPatterns";
import { BaseProfileComponent } from "../base-profile.component";
import { ProfileMapper } from "../profile-mapper";
import { EsCommonService } from "src/app/core/api-services/shared/es-common.service";
import { EsOtpDto } from "src/app/core/api-services/shared/EsOtpDto";

@Component({
    selector: "profile-company-owners",
    templateUrl: "profile-company-owners.component.html",
    styleUrls: ['./profile-company-owners.component.scss'],
})

export class ProfileCompanyOwnersComponent extends BaseProfileComponent implements OnInit{
    @Input() mainForm: FormGroup;
    @Input() owners: AuthorityOwnerModel[];
    @Input() lookups: UserProfileLookups;
    @Input() isRegistered: boolean = false;
    @Input() isRequired: boolean = true;
    @Input() isPowerUser: boolean = false;

    get activeOwners() : AuthorityOwnerModel[] {
        return this.mainForm.controls.Owners?.value?.filter((o : AuthorityOwnerModel)=> o.IsDeleted == null || o.IsDeleted==false);
    }
    
    form: FormGroup;
    showForm: boolean = false;
    isNew: boolean = false;
    isEditable: boolean = false;
    showControls: boolean = false;
    otpId: string;
    otpAlert: AlertOptions;
    otpAlertType: AlertImportanceEnum;
    selectedOwner: AuthorityOwnerModel;
    searchText: string;
    currentPage: number = 1;
    itemsPerPage: number = 10;
    public isCollapsed: boolean[] = [];

    constructor(
        translateService: TranslateService,
        protected _mapper: ProfileMapper,
        private _registrationService: RegistrationService,
        private _commonService: EsCommonService,
    ){
        super(translateService,_mapper);
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    updateSearchText($event: any) {
        this.currentPage = 1;
        this.searchText = $event.searchText;
    }

    addOwnerClick(){
        this.isNew = this.showForm = true;
        this.initializeForm();
    }

    cancel(){
        this.showForm = this.isNew = false; 
    }

    save(){
        if(this.form.valid){
            if(this.isNew){
                this.mainForm.controls.Owners?.value?.push(this.updateOwner(this.form.getRawValue()));
            }
            else{
                Object.assign(this.selectedOwner,this.updateOwner(this.form.getRawValue()));
                if(this.selectedOwner.Id){
                    this.selectedOwner.IsUpdated = true;
                }
            }
            this.resetForm();
            this.showForm = this.showControls = this.isEditable = false;
        }
        else{
            this.form.markAllAsTouched();
        }
    }

    edit($event: any,owner: AuthorityOwnerModel){
        this.isNew = false;
        this.showForm = true;
        this.selectedOwner = owner;
        this.initializeForm();
        this.partnershipTypeChange(owner.PartnershipType.code);
        if(owner.IdType){
            this.idTypeChange(owner.IdType);
            if(owner.IdType == "EID"){
                this.showControls = true;
                this.isEditable = false;
            }
        }
        this.setFormValue(owner);
    }

    delete(owner: AuthorityOwnerModel){
        if(owner.Id){
            owner.IsDeleted = true;
        }
        else{
            this.mainForm.controls.owners?.value?.splice(this.owners.indexOf(owner),1);
        }
    }

    onPartnershipTypeChange(){
        this.resetForm(["PartnershipType"]);
        this.partnershipTypeChange(this.form.controls.PartnershipType.value?.code);
    }

    onIdTypeChange($event:any){
        this.resetForm(["PartnershipType","IdType"]);
        this.otpAlert = null;
        if($event.new){
            this.idTypeChange($event.new);
        }
    }

    async onIDChange() {
        this.resetForm(["PartnershipType","IdType","IdNumber"]);
        this.showControls = this.isEditable = false;
        var idNumber = this.form.controls.IdNumber.value;

        if (idNumber && idNumber.length >= 18) {
            this._registrationService.ValidateIdNumber(idNumber)
            .subscribe(response=>{
                if(response?.Data?.IsValid){
                    this.otpId = response.Data.RegistrationID;
                    this.otpAlertType = AlertImportanceEnum.Info;
                    this.otpAlert = {
                        display: true,
                        text: this.translateService.instant('trApplicantOtpSentMessage', {
                            otpMobileNo: response.Data.MobileNumber,
                        })
                    }
                    this.form.addControl("OTP",new FormControl());
                }
                else{
                    this.otpId = null;
                    this.otpAlertType = AlertImportanceEnum.Danger;
                    this.otpAlert = {
                        display: true,
                        text: this.translateService.instant(`trApplicantEmiratesIdErrors.${response.Data.Status}`),
                    }
                    this.form.removeControl("OTP");
                }
            });
        }
    }

    async onOTPChange(){
        var otp = this.form.controls.OTP.value;
        if(otp && otp.length >= 5){
            var otpModel:EsOtpDto = {
                RegistrationID: this.otpId,
                OTP: otp
            }
            this._registrationService.ValidateOTP(otpModel)
            .subscribe(response=>{
                if(response?.Data){
                    this.otpAlert = null;
                    this.form.removeControl("OTP");
                    this.showControls = true;
                    this.isEditable = false;
                    this.form.controls.NameAr.setValue(response.Data.NameAr);
                    this.form.controls.NameEn.setValue(response.Data.Name);
                    this.form.controls.Nationality.setValue(
                        this.lookups.Countries.filter(c=>c.id == response.Data.CountryId)?.[0]
                    );
                    this.form.controls.Mobile.setValue(response.Data.Phone);
                }
                else{
                    this.otpAlertType = this.Danger;
                    this.otpAlert = {
                        key: "",
                        display: true
                    }
                }
            });
        }
    }

    private initializeForm(){
        this.form = new FormGroup({
            PartnershipType: new FormControl(null,Validators.required),
            NameAr: new FormControl(null,[Validators.required,Validators.pattern(InputPatterns.NonNumbericEnglishOrArabic)]),
            NameEn: new FormControl(null,[Validators.required,Validators.pattern(InputPatterns.NonNumbericEnglish)]),
            Nationality: new FormControl(null,Validators.required),
            Mobile: new FormControl(null,Validators.required),
        });
        this.isEditable = this.showControls = false;
    }

    private partnershipTypeChange(partnershipTypeCode: string){
        this.showControls = this.isEditable = false;
        if(partnershipTypeCode == "GOV" || partnershipTypeCode.indexOf("Company") > -1){
            this.form.removeControl("IdType");
            this.form.removeControl("Passport");
            this.form.removeControl("IdNumber");
            this.form.removeControl("OTP");
            this.showControls = this.isEditable = true;
        }
        else{
            this.form.addControl("IdType",new FormControl(null,Validators.required));
        }
    }

    private idTypeChange(idType: string){
        if(idType == "EID")
        {
            this.form.removeControl("Passport");
            this.form.addControl("IdNumber",new FormControl(null,Validators.required));
            this.showControls = this.isEditable = false;
        }
        else if(idType == "Passport"){
            this.form.removeControl("IdNumber");
            this.form.removeControl("OTP");
            this.form.addControl("Passport",new FormControl(null,Validators.required));
            this.showControls = this.isEditable = true;
        }
    }

    private updateOwner(owner: AuthorityOwnerModel): AuthorityOwnerModel{
        owner.PartnershipTypeId = owner.PartnershipType.id;
        owner.NationalityId = owner.Nationality.id;
        return owner;
    }
}